import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Clock, MapPin, Sparkles, X } from 'lucide-react'
import { useCityStore } from '@/store/cityStore'
import { useRecentSearchStore } from '@/store/recentSearchStore'
import { useFilterOptions } from '@/hooks/api/useFilterOptions'
import { areaFilterValue, topAreasByCount } from '@/lib/areaFilterUtils'
import { titleCase, buildPropertyHref } from '@/lib/propertyNavLinks'

const chipClass =
  'inline-flex shrink-0 items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 transition-colors hover:border-primary-200 hover:text-primary-600'

const popularChipClass =
  'inline-flex shrink-0 items-center gap-1.5 rounded-full border border-primary-100 bg-primary-50 px-3 py-1.5 text-xs font-semibold text-primary-700 transition-colors hover:border-primary-200 hover:bg-primary-100'

type HeroQuickLinksProps = {
  /** Active hero tab intent: buy | rent | commercial */
  intent?: string
  /** Tab category scope (New Project, plots) */
  category?: string
}

export function HeroQuickLinks({ intent = 'buy', category }: HeroQuickLinksProps) {
  const { cityName } = useCityStore()
  const { items, clear } = useRecentSearchStore()
  const { options } = useFilterOptions(cityName)
  const city = cityName.toLowerCase()

  const popularChips = useMemo(() => {
    const chips: { label: string; href: string; pin?: boolean }[] = []
    const isRent = intent === 'rent'
    const isCommercial = intent === 'commercial'
    const listingIntent = isRent ? 'rent' : isCommercial ? 'commercial' : 'buy'
    const np = (options.categories ?? []).find((c) => /new\s*project/i.test(c.name))
    const plotsCat = (options.categories ?? []).find((c) => /plot|land/i.test(c.name) && /plot|land/i.test(c.slug ?? c.name))

    // Primary chip matches the active tab (never show both Buy + Rent together).
    if (isRent) {
      chips.push({
        label: `Rent in ${cityName}`,
        href: buildPropertyHref('rent', { city }),
        pin: true,
      })
    } else if (isCommercial) {
      chips.push({
        label: `Commercial in ${cityName}`,
        href: buildPropertyHref('commercial', { city }),
        pin: true,
      })
    } else if (category === 'plots') {
      chips.push({
        label: `Plots in ${cityName}`,
        href: buildPropertyHref('buy', {
          city,
          category: plotsCat?.name ?? 'plots',
          categorySlug: plotsCat?.slug || 'plots',
        }),
        pin: true,
      })
    } else if (category === 'New Project') {
      chips.push({
        label: `New Projects in ${cityName}`,
        href: buildPropertyHref('buy', {
          city,
          category: np?.name ?? 'New Project',
          categorySlug: np?.slug || 'new-project',
        }),
        pin: true,
      })
    } else {
      chips.push({
        label: `Buy in ${cityName}`,
        href: buildPropertyHref('buy', { city }),
        pin: true,
      })
    }

    // BHK + verified for residential buy/rent tabs
    if (!isCommercial && category !== 'plots') {
      const bhk = (options.bhkOptions ?? [])
        .filter((b) => /^(2|3)\s*BHK$/i.test(b.trim()))
        .slice(0, 2)
      for (const label of bhk) {
        chips.push({
          label,
          href: buildPropertyHref(listingIntent === 'commercial' ? 'buy' : listingIntent, {
            city,
            bhk: label,
            ...(category === 'New Project'
              ? { category: np?.name, categorySlug: np?.slug || 'new-project' }
              : {}),
          }),
        })
      }

      chips.push({
        label: 'QBC Verified',
        href: buildPropertyHref(isRent ? 'rent' : 'buy', { city, verified: 'true' }),
      })
    }

    for (const area of topAreasByCount(options.areas ?? [], cityName, 3)) {
      chips.push({
        label: titleCase(area.name),
        href: buildPropertyHref(isCommercial ? '' : isRent ? 'rent' : 'buy', {
          city,
          area: areaFilterValue(area),
          ...(category === 'plots'
            ? { category: plotsCat?.name ?? 'plots', categorySlug: plotsCat?.slug || 'plots' }
            : {}),
          ...(category === 'New Project'
            ? { category: np?.name, categorySlug: np?.slug || 'new-project' }
            : {}),
        }),
        pin: true,
      })
    }

    return chips
  }, [cityName, city, options, intent, category])

  return (
    <div className="overflow-visible rounded-b-2xl border-t border-neutral-100 bg-neutral-50 px-4 py-3 md:px-5 md:py-3.5">
      {items.length > 0 && (
        <div className="mb-3 flex flex-col gap-2 border-b border-neutral-100 pb-3 sm:flex-row sm:items-center">
          <div className="flex shrink-0 items-center gap-1.5">
            <Clock className="h-3.5 w-3.5 text-primary-500" />
            <span className="text-xs font-semibold text-neutral-600">Recent</span>
            <button
              type="button"
              onClick={clear}
              aria-label="Clear recent searches"
              className="rounded-full p-1 text-neutral-400 hover:bg-white hover:text-neutral-600 sm:ml-1"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="flex min-w-0 flex-1 flex-wrap gap-2 py-0.5">
            {items.slice(0, 4).map((item) => (
              <Link key={item.id} to={item.href} className={chipClass}>
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3">
        <span className="flex shrink-0 items-center gap-1.5 pt-1.5 text-xs font-semibold text-neutral-600">
          <Sparkles className="h-3.5 w-3.5 shrink-0 text-amber-500" />
          Popular
        </span>
        <div className="flex min-w-0 flex-1 flex-wrap gap-2 py-0.5">
          {popularChips.map((chip) => (
            <Link key={`${chip.label}-${chip.href}`} to={chip.href} className={popularChipClass}>
              {chip.pin && <MapPin className="h-3 w-3 shrink-0 text-primary-500" />}
              {chip.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}
